import React, { useEffect, useState, useMemo } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
  CircularProgress,
  Box,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import sh from "bundles/common/utils/sh";

const PipelinePickerDialog = ({
  open,
  onClose,
  onConfirm,
  title = "Select pipelines",
  confirmLabel = "Add to pipelines",
  loading: externalLoading = false,
}) => {
  const [pipelines, setPipelines] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected([]);
    setSearch("");
    setFetching(true);
    sh.get("/internal/search/profile_pipelines")
      .then((res) => {
        setPipelines(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => setPipelines([]))
      .finally(() => setFetching(false));
  }, [open]);

  const filtered = useMemo(() => {
    if (!search.trim()) return pipelines;
    const q = search.toLowerCase();
    return pipelines.filter((p) =>
      (p.title || "").toLowerCase().includes(q),
    );
  }, [pipelines, search]);

  const toggleSelection = (pipeline) => {
    setSelected((prev) => {
      const exists = prev.find((s) => s.id === pipeline.id);
      if (exists) return prev.filter((s) => s.id !== pipeline.id);
      return [...prev, pipeline];
    });
  };

  const handleConfirm = () => {
    if (selected.length === 0) return;
    onConfirm(selected);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "0.75rem",
          border: "1px solid hsl(140 8% 88%)",
          backgroundColor: "#ffffff",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
          fontSize: 16,
          color: "hsl(150 30% 12%)",
          pb: 1,
        }}
      >
        {title}
      </DialogTitle>
      <DialogContent sx={{ pb: 1 }}>
        <TextField
          size="small"
          fullWidth
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search pipelines…"
          sx={{
            mb: 1.5,
            "& .MuiOutlinedInput-root": {
              borderRadius: "calc(-2px + 0.75rem)",
              fontSize: 13,
              fontFamily: "Segoe UI Regular, sans-serif, system-ui",
              backgroundColor: "hsl(140 10% 95%)",
              "& fieldset": { borderColor: "hsl(140 8% 88%)" },
              "&:hover fieldset": { borderColor: "hsl(140 8% 82%)" },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: "hsl(150 10% 46%)" }} />
              </InputAdornment>
            ),
          }}
        />

        {fetching ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} sx={{ color: "hsl(150 38% 17%)" }} />
          </Box>
        ) : filtered.length === 0 ? (
          <Typography
            variant="body2"
            sx={{
              py: 3,
              textAlign: "center",
              fontFamily: "Segoe UI Regular, sans-serif, system-ui",
              fontSize: 13,
              color: "hsl(150 10% 46%)",
            }}
          >
            No pipelines found
          </Typography>
        ) : (
          <List
            sx={{
              maxHeight: 280,
              overflow: "auto",
              mx: -1,
              "&::-webkit-scrollbar": { width: 6 },
              "&::-webkit-scrollbar-thumb": { background: "hsl(140 8% 82%)", borderRadius: 3 },
            }}
          >
            {filtered.map((p) => {
              const isSelected = selected.some((s) => s.id === p.id);
              return (
                <ListItemButton
                  key={p.id}
                  selected={isSelected}
                  onClick={() => toggleSelection(p)}
                  disableRipple
                  sx={{
                    borderRadius: "calc(-2px + 0.75rem)",
                    mx: 1,
                    mb: 0.5,
                    transition: "all 0.15s ease",
                    "&:hover": { backgroundColor: "hsl(140 10% 95%)" },
                    ...(isSelected
                      ? {
                        backgroundColor: "hsla(150, 38%, 17%, 0.08)",
                        "&:hover": { backgroundColor: "hsla(150, 38%, 17%, 0.12)" },
                      }
                      : {}),
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Checkbox
                      edge="start"
                      checked={isSelected}
                      disableRipple
                      size="small"
                      sx={{ color: "hsl(150 10% 46%)", "&.Mui-checked": { color: "hsl(150 38% 17%)" } }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={p.title}
                    primaryTypographyProps={{
                      sx: {
                        fontSize: 13,
                        fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                        color: "hsl(150 30% 12%)",
                      },
                    }}
                    secondary={p.pipeline_type === "shared" ? "Shared" : p.pipeline_type === "job_offer" ? "Job offer" : "Personal"}
                    secondaryTypographyProps={{
                      sx: {
                        fontSize: 12,
                        fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                        color: "hsl(150 10% 46%)",
                      },
                    }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, borderTop: "1px solid hsl(140 8% 88%)", pt: 1.5 }}>
        <Button
          onClick={onClose}
          disableRipple
          sx={{
            textTransform: "none",
            fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
            fontSize: 13,
            color: "hsl(150 10% 46%)",
            borderRadius: "calc(-2px + 0.75rem)",
            "&:hover": { backgroundColor: "hsl(140 10% 95%)", color: "hsl(150 30% 12%)" },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disableElevation
          disableRipple
          disabled={selected.length === 0 || externalLoading}
          onClick={handleConfirm}
          sx={{
            textTransform: "none",
            fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
            fontSize: 13,
            backgroundColor: "hsl(150 38% 17%)",
            color: "hsl(80 97% 72%)",
            borderRadius: "calc(-2px + 0.75rem)",
            transition: "opacity 0.15s ease",
            "&:hover": { backgroundColor: "hsl(150 38% 17%)", opacity: 0.9 },
            "&:disabled": { backgroundColor: "hsla(150, 38%, 17%, 0.4)", color: "hsla(80, 97%, 72%, 0.6)" },
          }}
        >
          {externalLoading ? <CircularProgress size={20} sx={{ color: "hsl(80 97% 72%)" }} /> : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PipelinePickerDialog;
